import { useMemo } from 'react'
import { currentReadings, type Reading, type Target } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import type { Pending } from '../sync/merge.ts'

/** The measurement notebook as the device holds it (M13, ADR-100): every reading of the club, mirrored, plus the readings this device has taken and not yet sent (they carry `_pending`). */
export interface MeasurementRow extends Reading {
  site_id: string; actor_person_id?: string | null; area_version_id: string | null; value_entered: number; unit_entered: string; unit_code: string; entered_at: string
  note: string | null; source: 'manual' | 'import'; import_batch_id: string | null; corrects_measurement_id: string | null; correction_reason: string | null
}
export interface TargetRow extends Target { id: string; revision: number; note: string | null; archived_at: string | null }

export const useMeasurements = () => useMerged<MeasurementRow>('measurement', 'ops.measurement')
export const useTargets = () => useMerged<TargetRow>('measurement_target', 'ops.measurement_target')

/** The notebook, ready for a screen: readings that count (a corrected one is replaced by its correction), the club's live targets. */
export function useNotebook() {
  const readings = useMeasurements()
  const targets = useTargets()
  return useMemo(() => ({
    ready: !!readings && !!targets,
    all: readings ?? [],
    readings: readings ? currentReadings(readings) : [],
    targets: (targets ?? []).filter((x) => !x.archived_at),
    allTargets: targets ?? [],
  }), [readings, targets])
}
export type NotebookReading = Pending<MeasurementRow>
